/**
 * Shared constants for the Electron app
 */

/**
 * Duration in seconds for capturing a process sample
 * Used by both the Electron main process and renderer process
 */
export const PROCESS_SAMPLE_DURATION_SECONDS = 5;
export const BLOCKED_NAVIGATION_IFRAME_NAME_PREFIX =
  'littlebird-blocked-navigation-iframe-';
export const EXTERNAL_NAVIGATION_IFRAME_NAME_PREFIX =
  'littlebird-external-navigation-iframe-';
export const SAME_ORIGIN_NAVIGATION_IFRAME_NAME_PREFIX =
  'littlebird-same-origin-navigation-iframe-';

/**
 * Bundle identifiers for the VS Code family of editors. Kept in sync with
 * the `*_BUNDLE_IDENTIFIER` vars in `JSParsers/vscode/vscode_parser.js`,
 * `JSParsers/cursor/cursor_parser.js`, and
 * `JSParsers/antigravityide/antigravityide_parser.js`.
 */
export const VS_CODE_BUNDLE_ID = 'com.microsoft.VSCode' as const;
export const CURSOR_BUNDLE_ID = 'com.todesktop.230313mzl4w4u92' as const;
export const ANTIGRAVITY_IDE_BUNDLE_ID = 'com.google.antigravity-ide' as const;
export const WINDSURF_BUNDLE_ID = 'com.exafunction.windsurf' as const;

/**
 * App-specific copy for `vscode-family-screenreader-not-enabled` toasts. Keyed
 * by bundle identifier so the renderer can switch UI based on whether the
 * affected editor was VS Code, Cursor, etc.
 */
export const VS_CODE_FAMILY_VARIANTS = {
  [VS_CODE_BUNDLE_ID]: {
    appName: 'VS Code',
    contentKey: 'vscode-accessibility',
    hash: 'vscode-accessibility',
  },
  [CURSOR_BUNDLE_ID]: {
    appName: 'Cursor',
    contentKey: 'cursor-accessibility',
    hash: 'cursor-accessibility',
  },
  [ANTIGRAVITY_IDE_BUNDLE_ID]: {
    appName: 'Antigravity IDE',
    contentKey: 'antigravity-accessibility',
    hash: 'antigravity-accessibility',
  },
  [WINDSURF_BUNDLE_ID]: {
    appName: 'Windsurf',
    contentKey: 'windsurf-accessibility',
    hash: 'windsurf-accessibility',
  },
} as const;

export type VSCodeFamilyVariant =
  (typeof VS_CODE_FAMILY_VARIANTS)[keyof typeof VS_CODE_FAMILY_VARIANTS];

/**
 * Resolve a bundle identifier to its VS Code family variant copy. Returns
 * `null` for unknown bundles so callers can decide how to surface the issue
 * rather than silently misattributing it to VS Code.
 */
export function vsCodeFamilyVariant(
  bundleIdentifier: string | undefined
): VSCodeFamilyVariant | null {
  if (bundleIdentifier && bundleIdentifier in VS_CODE_FAMILY_VARIANTS) {
    return VS_CODE_FAMILY_VARIANTS[
      bundleIdentifier as keyof typeof VS_CODE_FAMILY_VARIANTS
    ];
  }
  return null;
}
